// Deal-Pipeline / Quotes-table activity cell: a glance (last type icon + date
// + count) with a hover popover showing the full notes+activities feed and a
// quick "Log" button. Everything anchored to the person behind the row.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Loader2, Activity } from "lucide-react";
import api from "../../lib/api";
import { metaFor, activityToneCls } from "./activityMeta";
import ActivityLogModal from "./ActivityLogModal";

function relDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < 0) return "soon";
  if (diff < day) return "today";
  if (diff < 2 * day) return "1d ago";
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

function Popover({ rect, personId, onClose, onHoverKeep }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/crm/persons/${personId}/activity-feed?limit=8`)
      .then((r) => { if (alive) setItems(r.data.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [personId]);

  const top = Math.min(rect.bottom + 6, window.innerHeight - 20);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320));

  return createPortal(
    <div
      style={{ position: "fixed", top, left, width: 300, zIndex: 95 }}
      onMouseEnter={onHoverKeep}
      onMouseLeave={onClose}
      data-testid="activity-popover"
      className="rounded-lg border border-white/12 bg-[#0F1420]/98 backdrop-blur-xl shadow-2xl p-2.5 max-h-[320px] overflow-y-auto"
    >
      {items === null && (
        <div className="flex items-center gap-2 text-[11px] text-slate-400 py-2 px-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      )}
      {items && items.length === 0 && (
        <div className="text-[11px] text-slate-500 font-mono py-2 px-1">No notes or activities yet.</div>
      )}
      {items && items.map((it) => {
        const Icon = metaFor(it.activity_type).icon;
        return (
          <div key={`${it.kind}-${it.id}`} className="flex items-start gap-2 py-1.5 border-b border-white/[0.05] last:border-0">
            <span className={`shrink-0 mt-0.5 w-5 h-5 grid place-items-center rounded border ${activityToneCls(it.activity_type)}`}>
              <Icon className="w-3 h-3" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-slate-200 leading-snug truncate">
                {it.subject || (it.content ? it.content.slice(0, 60) : it.activity_type)}
                {it.completed && <span className="ml-1 text-emerald-400 text-[10px]">✓</span>}
              </div>
              {it.subject && it.content && (
                <div className="text-[10.5px] text-slate-400 leading-snug line-clamp-2">{it.content.slice(0, 90)}</div>
              )}
              <div className="text-[9.5px] text-slate-500 font-mono mt-0.5 flex flex-wrap gap-x-1.5">
                <span>{it.activity_type}</span>
                <span>· {fmtWhen(it.created_at)}</span>
                {it.created_by_name && <span>· {it.created_by_name}</span>}
                {it.ai_assisted && <span className="text-violet-300" title="Filled with voice / AI help, approved by the author">· AI-assisted</span>}
                {it.assignee_name && <span>→ {it.assignee_name}</span>}
                {it.due_date && <span className="text-amber-300">· due {fmtWhen(it.due_date)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

export default function ActivityCell({ personId, am, summary, onSaved, testIdSuffix = "", readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [modal, setModal] = useState(false);
  const cellRef = useRef(null);
  const closeTimer = useRef(null);

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (cellRef.current) setRect(cellRef.current.getBoundingClientRect());
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const keep = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };

  const count = summary?.count || 0;
  const overdue = summary?.overdue || 0;
  const lastType = summary?.last_type || "Other";
  const LastIcon = metaFor(lastType).icon;

  return (
    <div
      ref={cellRef}
      className="inline-flex items-center gap-1.5"
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={(e) => e.stopPropagation()}
      data-testid={`activity-cell-${testIdSuffix || personId}`}
    >
      {count > 0 ? (
        <button
          type="button"
          onClick={() => !readOnly && setModal(true)}
          className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono hover:brightness-125 ${overdue ? "bg-rose-500/15 text-rose-200 border-rose-500/50 animate-pulse" : activityToneCls(lastType)}`}
          title={overdue ? `${overdue} overdue · ${count} logged — follow up now` : `${count} logged · last ${summary?.last_subject || lastType}`}
          data-testid={`activity-chip-${testIdSuffix || personId}`}
        >
          {overdue > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 grid place-items-center rounded-full bg-rose-500 text-white text-[8px] font-bold leading-none ring-2 ring-[#0b0f17]"
              data-testid={`activity-overdue-badge-${testIdSuffix || personId}`}
            >{overdue}</span>
          )}
          <LastIcon className="w-3 h-3" />
          <span>{relDate(summary?.last_at)}</span>
          <span className="ml-0.5 px-1 rounded-full bg-black/30 text-slate-200">{count}</span>
        </button>
      ) : readOnly ? <span className="text-[10px] text-slate-600">—</span> : (
        <button
          type="button"
          onClick={() => setModal(true)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/10 text-slate-500 hover:text-purple-300 hover:border-purple-500/40 text-[10px] font-mono"
          title="Log an activity or note"
          data-testid={`activity-log-btn-${testIdSuffix || personId}`}
        >
          <Plus className="w-3 h-3" /> Log
        </button>
      )}
      {count > 0 && !readOnly && (
        <button
          type="button"
          onClick={() => setModal(true)}
          className="w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-purple-300 hover:bg-purple-500/10"
          title="Log an activity or note"
          data-testid={`activity-log-btn-${testIdSuffix || personId}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}

      {open && rect && count > 0 && (
        <Popover rect={rect} personId={personId} onClose={hide} onHoverKeep={keep} />
      )}

      {modal && (
        <ActivityLogModal
          personId={personId}
          kind="activity"
          defaultAssigneeName={am}
          onClose={() => setModal(false)}
          onSaved={async () => { setModal(false); await onSaved?.(); }}
        />
      )}
    </div>
  );
}
